import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ShoppingCart, AlertTriangle, FileText } from 'lucide-react';
import PriceBreakdown from '../components/PriceBreakdown';
import MultiDayDiscountInfo from '../components/MultiDayDiscountInfo';
import AddonQuantitySelector from '../components/AddonQuantitySelector';
import OptionalComponentSelector, { isIncludeItemRemoved } from '../components/OptionalComponentSelector';
import { Product, BookingDate, CartAddon } from '../types';
import { useCart } from '../context/CartContext';
import { supabase } from '../lib/supabaseClient';
import { mapDbProductRow } from '../utils/mapDbProduct';
import ProductAvailabilityCalendar from '../components/ProductAvailabilityCalendar';
import CustomerQuoteRequestForm from '../components/CustomerQuoteRequestForm';
import { formatDateLocal } from '../utils/dateUtils';
import { useEmailSettings } from '../hooks/useEmailSettings';
import {
  clampProductQuantity,
  getProductMaxQuantity,
  getProductMinQuantity,
  isDjBookingProduct,
} from '../utils/bookingFlowProducts';
import {
  blocksBookingSubmission,
  inventoryCoveragePressureMessage,
  normalizeAvailabilityMode,
} from '../utils/productAvailability';
import ProductNewRibbon from '../components/ProductNewRibbon';
import { trackBookingView } from '../utils/siteAnalytics';

const BookingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { settings } = useEmailSettings();
  
  const [product, setProduct] = useState<Product | null>(null);
  const bookingViewTrackedForId = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedUpgrades, setSelectedUpgrades] = useState<string[]>([]);
  const [removedComponents, setRemovedComponents] = useState<string[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<{ [key: string]: number }>({});
  const [productAddons, setProductAddons] = useState<any[]>([]);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{
    available: boolean;
    status: string;
    message: string;
    unavailableItems?: Array<{
      inventory_item_id?: string;
      product_id?: string;
      name: string;
      status: string;
      message: string;
    }>;
    unavailableTypes?: Array<{
      inventory_type_id: string;
      name: string;
      available: number;
      required: number;
      message: string;
      is_substitutable: boolean;
    }>;
    unavailableItems?: Array<{
      product_id: string;
      name: string;
      status: string;
      message: string;
    }>;
  } | null>(null);
  
  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} - ${settings.company_name || 'Webbplats'}`;
    }
  }, [product, settings.company_name]);

  useEffect(() => {
    if (!product?.id) return;
    if (bookingViewTrackedForId.current === product.id) return;
    bookingViewTrackedForId.current = product.id;
    trackBookingView(product);
  }, [product]);

  useEffect(() => {
    // Check availability when dates change
    if (selectedStartDate && selectedEndDate && product) {
      checkAvailability();
    } else {
      setAvailabilityStatus(null);
    }
  }, [selectedStartDate, selectedEndDate, product, quantity]);

  const fetchProduct = async () => {
    if (!id) return;

    try {
      console.log('Fetching product with ID:', id);

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        console.log('Product data:', data);

        const transformedProduct = mapDbProductRow(data as Record<string, unknown>);

        setProduct(transformedProduct);
        setQuantity(getProductMinQuantity(transformedProduct));
        // Fetch product addons
        await fetchProductAddons(data.id);
      } else {
        // Product not found
        setError('Produkten hittades inte');
      }
    } catch (err: any) {
      console.error('Error fetching product:', err);
      setError('Ett fel uppstod när produkten skulle hämtas');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductAddons = async (productId: string) => {
    try {
      // Use optimized view for better performance
      const { data, error } = await supabase
        .from('product_addons_with_details')
        .select('*')
        .eq('product_id', productId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // Use flat data directly from view - no transformation needed
      setProductAddons(data || []);
    } catch (err) {
      console.error('Error fetching product addons:', err);
      setProductAddons([]);
    }
  };

  const checkAvailability = async () => {
    if (!product || !selectedStartDate || !selectedEndDate) return;
  
    try {
      const startDateStr = formatDateLocal(selectedStartDate);
      const endDateStr = formatDateLocal(selectedEndDate);

      console.log('Checking availability for:', {
        productId: product.id,
        startDate: startDateStr,
        endDate: endDateStr,
        quantity: quantity
      });

      // Commercial Core: basic availability (inga inventory-tabeller)
      const { data, error } = await supabase.rpc(
        'check_basic_product_availability',
        {
          p_product_id: product.id,
          p_start_date: startDateStr,
          p_end_date: endDateStr,
          p_quantity: quantity
        }
      );

      if (error) {
        console.error('RPC error:', error);
        // Fallback till tillgänglig om RPC misslyckas
        setAvailabilityStatus({
          available: true,
          status: 'available',
          message: 'Produkten är tillgänglig (kunde inte kontrollera lagerstatus)'
        });
        return;
      }

      console.log('Detailed availability check result:', data);

      // Parsa svaret från RPC-funktionen
      if (typeof data === 'object' && data !== null) {
        setAvailabilityStatus({
          available: data.available || false,
          status: data.status || 'unknown',
          message: data.message || 'Okänd status',
          unavailableItems: data.unavailable_items || [],
          unavailableTypes: data.unavailable_types || []
        });
      } else {
        // Fallback om svaret inte är som förväntat
        setAvailabilityStatus({
          available: true,
          status: 'available',
          message: 'Produkten är tillgänglig'
        });
      }
    } catch (err: any) {
      console.error('Error checking availability:', err);
      // On error, default to available to not block bookings
      setAvailabilityStatus({
        available: true,
        status: 'available',
        message: 'Produkten är tillgänglig (fel vid tillgänglighetskontroll)'
      });
    }
  };
  
  const handleDateSelection = (startDate: Date | null, endDate: Date | null) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const handleUpgradeToggle = (upgradeName: string) => {
    setSelectedUpgrades(prev => {
      if (prev.includes(upgradeName)) {
        return prev.filter(name => name !== upgradeName);
      } else {
        return [...prev, upgradeName];
      }
    });
  };

  // Convert selectedAddons to CartAddon objects for price calculations
  const cartAddons = useMemo((): CartAddon[] => {
    console.log('🔄 cartAddons useMemo recalculating', {
      selectedAddons,
      productAddonsLength: productAddons.length,
      productAddonsIds: productAddons.map(a => a.id)
    });
    const result: CartAddon[] = [];
    Object.entries(selectedAddons).forEach(([addonId, quantity]) => {
      if (quantity > 0) {
        const addonData = productAddons.find(a => a.id === addonId);
        console.log(`  Processing addon ${addonId}:`, {
          quantity,
          addonData: addonData ? 'found' : 'not found',
          addonDataSample: addonData ? { id: addonData.id, type: addonData.addon_type, name: addonData.template_name || addonData.product_name } : null
        });

        if (addonData) {
          const isTemplate = addonData.addon_type === 'template';
          const name = isTemplate ? addonData.template_name : addonData.product_name;
          const pricePerUnit = addonData.custom_price !== null && addonData.custom_price !== undefined
            ? addonData.custom_price
            : parseFloat(addonData.effective_price || '0');
          const pricingType = isTemplate ? addonData.template_pricing_type : 'per_day';
          const unitLabel = isTemplate ? addonData.template_unit_label : 'dag';

          console.log(`    Addon details:`, { name, pricePerUnit, pricingType, unitLabel });

          if (name && pricePerUnit > 0) {
            const cartAddon: CartAddon = {
              id: addonId,
              name,
              quantity,
              pricePerUnit,
              pricingType: pricingType || 'fixed',
              unitLabel: unitLabel || 'st',
            };
            if (!isTemplate) {
              const catalogProductId = addonData.addon_product_id
                ? String(addonData.addon_product_id)
                : undefined;
              if (catalogProductId) cartAddon.addon_product_id = catalogProductId;
              const img = addonData.product_image ? String(addonData.product_image).trim() : '';
              if (img) cartAddon.image = img;
              const slug = addonData.product_slug ? String(addonData.product_slug).trim() : '';
              if (slug) cartAddon.slug = slug;
            }
            result.push(cartAddon);
          } else {
            console.log(`    ⚠️ Skipping addon: name=${name}, pricePerUnit=${pricePerUnit}`);
          }
        }
      }
    });

    console.log('✨ Final cartAddons:', result);
    return result;
  }, [selectedAddons, productAddons]);

  const handleAddToCart = () => {
    if (!product || !selectedStartDate || !selectedEndDate) return;

    const shouldBlock = availabilityStatus
      ? blocksBookingSubmission(product.availabilityMode, availabilityStatus.available)
      : false;

    if (shouldBlock) {
      // Inventory shortage must never reach here; kept as safety net for non-inventory blocks.
      setError('Bokningen kunde inte slutföras för valda datum. Försök igen eller kontakta oss.');
      return;
    }

    addToCart({
      product,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      quantity: clampProductQuantity(product, quantity),
      options: selectedUpgrades,
      removedComponents: removedComponents.length > 0 ? removedComponents : undefined,
      addons: cartAddons.length > 0 ? cartAddons : undefined
    });

    setBookingSuccess(true);

    setTimeout(() => {
      navigate('/kassa');
    }, 2000);
  };
  
  if (loading) {
    return (
      <div className="pb-16 min-h-screen flex items-center justify-center">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  if (error || !product) {
    return (
      <div className="pb-16 min-h-screen">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Produkten hittades inte</h1>
          <p className="text-gray-600 mb-6">Den produkt du söker verkar inte finnas i vårt sortiment.</p>
          <Link
            to="/produkter"
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors duration-300"
          >
            <ChevronLeft className="mr-2" size={18} />
            Tillbaka till produkter
          </Link>
        </div>
      </div>
    );
  }

  // Block booking for quote-based products
  if (product.priceOnRequest) {
    return (
      <div className="pb-16 min-h-screen bg-transparent relative overflow-hidden">
        {/* Animated background elements */}
        <div className="hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
          <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-900/35 border border-slate-800 p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-500/20 rounded-full mb-6">
                <FileText className="w-10 h-10 text-purple-300" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Denna produkt kräver offert
              </h1>
              <p className="text-gray-300 mb-6 text-lg">
                {product.name} är en skräddarsydd produkt som kräver en personlig offert.
              </p>
              {product.quoteFormMessage && (
                <div className="mb-6 p-4 bg-purple-500/20 border border-purple-500/30 rounded-none">
                  <p className="text-purple-300">{product.quoteFormMessage}</p>
                </div>
              )}
              <div className="mb-8 p-6 bg-blue-500/20 border border-blue-500/30 rounded-none">
                <div className="flex items-start justify-center">
                  <FileText className="text-blue-400 mr-3 mt-1 shrink-0" size={24} />
                  <div className="text-left">
                    <h3 className="text-white font-semibold mb-2 text-lg">
                      Vi skickar offert inom 24 timmar
                    </h3>
                    <p className="text-gray-300">
                      Fyll i dina uppgifter och beskriv ditt event så återkommer vi med en skräddarsydd offert anpassad efter dina önskemål.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setShowQuoteForm(true)}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-none transition-all duration-300 shadow-lg"
                >
                  <FileText className="mr-2" size={20} />
                  Begär offert
                </button>
                <Link
                  to={`/produkter/${product.slug || product.id}`}
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-8 rounded-none transition-colors duration-300 border border-white/20"
                >
                  <ChevronLeft className="mr-2" size={18} />
                  Tillbaka till produkt
                </Link>
              </div>
            </div>
          </div>
        </div>

        {showQuoteForm && (
          <CustomerQuoteRequestForm
            product={product}
            onClose={() => setShowQuoteForm(false)}
          />
        )}
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="pb-16 min-h-screen bg-transparent">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-md mx-auto bg-white p-8 rounded-none shadow-md">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <ShoppingCart className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Produkt tillagd i varukorgen!</h1>
            <p className="text-gray-600 mb-6">
              Du kommer nu att omdirigeras till kassan för att slutföra din bokning.
            </p>
            <div className="animate-pulse bg-gray-200 h-1 w-full rounded-full">
              <div className="bg-green-500 h-1 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Format date for display
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('sv-SE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  const djBooking = isDjBookingProduct(product);
  const availabilityMode = normalizeAvailabilityMode(product.availabilityMode);
  const blocksSubmission =
    availabilityStatus != null &&
    blocksBookingSubmission(availabilityMode, availabilityStatus.available);
  const backToProductHref =
    djBooking && product.hiddenFromProducts ? '/dj' : `/produkter/${product.slug || product.id}`;
  const backToProductLabel =
    djBooking && product.hiddenFromProducts ? 'Tillbaka till DJ' : 'Tillbaka till produktdetaljer';
  
  return (
    <div className="pb-16 min-h-screen bg-transparent relative overflow-hidden">
      <Helmet>
        <title>{product.name} – Boka | festutrustning.se</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`https://festutrustning.se/produkter/${product.slug || product.id}`} />
      </Helmet>
      {/* Animated background elements */}
      <div className="hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating particles */}
      <div className="hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-30 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>
      
      <div className="container mx-auto px-4">
        <div className="mb-6 relative z-10">
          <Link 
            to={backToProductHref} 
            className="inline-flex items-center text-purple-300 hover:text-purple-200 font-medium transition-colors"
          >
            <ChevronLeft size={18} className="mr-1" />
            {backToProductLabel}
          </Link>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
          <div>
            <div className="bg-slate-900/35 border border-slate-800 overflow-hidden mb-8">
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-4 text-white">Boka {product.name}</h1>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {djBooking ? (
                    <>
                      Välj datum för ditt event. Priset är fast för DJ-uppdraget enligt beskrivningen (en spelning) –
                      flerdagsrabatt för uthyrning gäller inte. Det avser endast DJ; festutrustning (t.ex. högtalare och ljus){' '}
                      ingår inte men kan hyras separat om ni behöver det. Röda datum är spärrade.
                    </>
                  ) : (
                    <>
                      Välj datum för din bokning i kalendern nedan. Vissa datum kan markeras med
                      begränsad tillgänglighet beroende på hur många du vill hyra – du kan ändå
                      skicka en förfrågan.
                    </>
                  )}
                </p>
                
                {!djBooking && <MultiDayDiscountInfo />}
                
                <ProductAvailabilityCalendar
                  productId={product.id}
                  requestedQuantity={quantity}
                  onSelectDates={handleDateSelection}
                />
              </div>
            </div>
          </div>
          
          <div>
            <div className="bg-slate-900/35 border border-slate-800 overflow-hidden mb-8">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4 text-white">Din bokning</h2>
                
                <div className="flex items-start mb-6">
                  <div className="relative w-20 h-20 flex-shrink-0 mr-4 overflow-visible">
                    <div className="absolute inset-0 overflow-hidden rounded-none bg-slate-800/30">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <ProductNewRibbon newUntil={product.newUntil} size="sm" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{product.name}</h3>
                    {djBooking ? (
                      <div className="text-gray-300 mt-1">
                        <p className="font-medium text-white">
                          {product.pricePerDay.toLocaleString('sv-SE')} kr
                        </p>
                        <p className="text-xs text-gray-400 mt-1 leading-snug">
                          Endast DJ enligt beskrivning. Festutrustning ingår inte.
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-300">{product.pricePerDay} kr/dag</p>
                    )}
                  </div>
                </div>
                
                {availabilityStatus && !availabilityStatus.available && (
                  <div className="bg-orange-100 border border-orange-400 text-orange-900 px-4 py-3 rounded mb-4 flex items-start">
                    <AlertTriangle className="mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="font-medium">
                        {availabilityStatus.status === 'blocked'
                          ? 'Datum spärrat'
                          : 'Eget lager kan vara otillräckligt'}
                      </p>
                      <p>
                        {availabilityStatus.status === 'blocked'
                          ? 'Detta datum är inte tillgängligt för bokning.'
                          : inventoryCoveragePressureMessage()}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="border-t border-b py-4 mb-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Valda datum
                    </label>
                    {selectedStartDate && selectedEndDate ? (
                      <p className="text-white">
                        {formatDate(selectedStartDate)} - {formatDate(selectedEndDate)}
                      </p>
                    ) : (
                      <p className="text-gray-400">Välj datum i kalendern</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-300 mb-1">
                      Antal
                    </label>
                    <select
                      id="quantity"
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(clampProductQuantity(product, Number(e.target.value)))
                      }
                      className="w-full p-2 border border-white/30 rounded-none focus:ring-purple-500 focus:border-purple-500 bg-white/10 backdrop-blur-sm text-white"
                    >
                      {Array.from(
                        {
                          length:
                            getProductMaxQuantity(product) - getProductMinQuantity(product) + 1,
                        },
                        (_, i) => getProductMinQuantity(product) + i
                      ).map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                    {getProductMinQuantity(product) > 1 && (
                      <p className="mt-1 text-xs text-gray-400">
                        Minimumhyra {getProductMinQuantity(product)} enheter
                      </p>
                    )}
                  </div>
                </div>

                {product.includes && product.includes.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Detta ingår</h3>
                    <ul className="space-y-1">
                      {product.includes.map((item, index) => {
                        const struck = isIncludeItemRemoved(
                          item,
                          product.optionalComponents || [],
                          removedComponents
                        );
                        return (
                          <li
                            key={index}
                            className={`text-sm ${struck ? 'text-gray-500 line-through' : 'text-gray-300'}`}
                          >
                            {item}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {product.optionalComponents && product.optionalComponents.length > 0 && (
                  <OptionalComponentSelector
                    components={product.optionalComponents}
                    removedComponents={removedComponents}
                    onChange={setRemovedComponents}
                  />
                )}
                
                {/* New addon system */}
                {productAddons && productAddons.length > 0 && (
                  <AddonQuantitySelector
                    productAddons={productAddons}
                    selectedAddons={selectedAddons}
                    onAddonsChange={(newAddons) => {
                      console.log('📝 Addons changed:', newAddons);
                      setSelectedAddons(newAddons);
                    }}
                    className="mb-6"
                  />
                )}

                <PriceBreakdown
                  key={JSON.stringify({ selectedAddons, removedComponents })}
                  pricePerDay={product.pricePerDay}
                  quantity={quantity}
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  upgrades={product.upgrades}
                  selectedUpgrades={selectedUpgrades}
                  addons={cartAddons}
                  optionalComponents={product.optionalComponents}
                  removedComponents={removedComponents}
                  product={product}
                  className="mb-6"
                />

                <div className="bg-blue-500/20 border border-blue-400/30 rounded-none p-4 mb-6">
                  <p className="text-sm text-blue-100 text-center">
                    💡 <strong>Tips:</strong> Eventuella rabattkoder kan användas i kassan
                  </p>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={!selectedStartDate || !selectedEndDate || blocksSubmission}
                  className={`w-full inline-flex justify-center items-center ${
                    !selectedStartDate || !selectedEndDate || blocksSubmission
                      ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  } font-bold py-4 px-6 rounded-none transition-colors duration-300`}
                >
                  <ShoppingCart className="mr-2" size={20} />
                  {availabilityStatus && !availabilityStatus.available
                    ? 'Skicka förfrågan'
                    : 'Lägg till i varukorg'}
                </button>
                
                {(!selectedStartDate || !selectedEndDate) && (
                  <p className="text-red-500 text-sm mt-2">
                    Välj och bekräfta datum i kalendern för att fortsätta
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(120deg); }
          66% { transform: translateY(10px) rotate(240deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default BookingPage;